// Generic data table used by every admin page.
// Columns: [{ key: 'field name in record' | (r)=>value, label, fmt?: (v,r)=>node, className?, testidKey? }]
import { useMemo, useState } from "react";
import { Search, ArrowDownAZ, Loader2 } from "lucide-react";

const fmtCurrency = (v) => {
  if (v && typeof v === "object" && "specialValue" in v) return "—";
  return typeof v === "number" ? `$${Math.round(v).toLocaleString()}` : v ?? "—";
};
const fmtDate = (v) => {
  if (!v) return "—";
  if (typeof v === "object" && "specialValue" in v) return "—";
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return typeof v === "string" ? v : "—";
    return d.toLocaleDateString();
  } catch {
    return "—";
  }
};

// Airtable returns {specialValue: 'NaN'|'Infinity'} for formula errors and
// {label, color} for some select shapes. Coerce safely for React rendering.
function safeRender(v) {
  if (v == null || v === "") return "—";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return v;
  if (Array.isArray(v)) return v.map(safeRender).join(", ");
  if (typeof v === "object") {
    if ("specialValue" in v) return "—";
    if ("name" in v) return v.name;
    if ("label" in v) return v.label;
    if ("text" in v) return v.text;
    if ("url" in v) return v.url;
    return "—";
  }
  return String(v);
}

export { fmtCurrency, fmtDate, safeRender };

export default function DataTable({
  loading,
  records = [],
  columns = [],
  searchKeys = [],
  emptyText = "No records yet.",
  testId = "data-table",
  initialSort = null,
}) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState(initialSort); // {key, dir}

  const filtered = useMemo(() => {
    let out = records;
    if (q && searchKeys.length) {
      const needle = q.toLowerCase();
      out = out.filter((r) => searchKeys.some((k) => String(r[k] ?? "").toLowerCase().includes(needle)));
    }
    if (sort && sort.key) {
      out = [...out].sort((a, b) => {
        const av = a[sort.key];
        const bv = b[sort.key];
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === "number" && typeof bv === "number") return sort.dir === "asc" ? av - bv : bv - av;
        return sort.dir === "asc"
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      });
    }
    return out;
  }, [records, q, sort, searchKeys]);

  const onSort = (k) => {
    setSort((s) => (s && s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "desc" }));
  };

  return (
    <div data-testid={testId} className="border border-jc-gold/20 bg-jc-black/40 backdrop-blur-sm">
      <div className="flex items-center justify-between gap-3 px-4 md:px-5 py-4 border-b border-jc-gold/15">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-jc-muted">
          {filtered.length} {filtered.length === 1 ? "record" : "records"}
        </div>
        {searchKeys.length > 0 && (
          <div className="flex items-center gap-2 border-b border-jc-gold/30 pb-1">
            <Search size={12} className="text-jc-gold" />
            <input
              data-testid={`${testId}-search`}
              placeholder="Search..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="bg-transparent text-sm font-mono text-jc-white placeholder-jc-muted focus:outline-none w-40 md:w-60"
            />
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left">
              {columns.map((c) => (
                <th key={c.label} className="px-4 md:px-5 py-3 font-mono text-[10px] uppercase tracking-[0.22em] text-jc-gold whitespace-nowrap border-b border-jc-gold/15">
                  {c.sortable ? (
                    <button onClick={() => onSort(typeof c.key === "string" ? c.key : c.label)} className="inline-flex items-center gap-1 hover:text-jc-gold-light">
                      {c.label} <ArrowDownAZ size={10} />
                    </button>
                  ) : (
                    c.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={columns.length} className="px-4 md:px-5 py-10 text-center text-jc-muted"><Loader2 size={18} className="inline-block animate-spin text-jc-gold" /></td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={columns.length} className="px-4 md:px-5 py-10 text-center font-mono text-xs uppercase tracking-[0.25em] text-jc-muted">{emptyText}</td></tr>
            )}
            {!loading && filtered.map((r, i) => (
              <tr key={r.id || i} data-testid={`${testId}-row-${i + 1}`} className="hover:bg-jc-navy/30 transition-colors">
                {columns.map((c) => {
                  const raw = typeof c.key === "function" ? c.key(r) : r[c.key];
                  const node = c.fmt ? c.fmt(raw, r) : safeRender(raw);
                  return (
                    <td key={c.label} className={`px-4 md:px-5 py-4 border-b border-jc-gold/10 font-body text-jc-white ${c.className || ""}`}>
                      {node}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Status pill helper used by tables
export function StatusPill({ value, tone = "neutral" }) {
  const tones = {
    success: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    warning: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    danger: "bg-red-500/15 text-red-300 border-red-500/30",
    info: "bg-jc-gold/15 text-jc-gold-light border-jc-gold/30",
    neutral: "bg-jc-navy/40 text-jc-muted border-jc-gold/15",
  };
  const text = safeRender(value);
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] font-mono border ${tones[tone] || tones.neutral}`}>
      {text === "—" ? "—" : text}
    </span>
  );
}
